import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    TextField,
    IconButton,
    Tooltip,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    List,
    ListItem,
    ListItemButton,
    Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import debounce from "lodash.debounce";
import PostalCodeForm from "../../Components/groups/PostalCodeForm";
import UserSearchInput from "../../Components/groups/UserSearchInput";
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import DescriptionIcon from '@mui/icons-material/Description';
import UsersReportList from "../../Components/groups/UsersReportList";
import SelectionMap from "../../Components/groups/SelectionMap";
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ImportUsersDialog from "../../Components/groups/ImportUsersButton";
import api from "../../API/interceptors";
import { useSelector } from "react-redux";

const GROUP_TYPES = {
    trainingGroup: { name: "Grupa szkoleniowa" }
}

const LOCALIZATION_TYPES = {
    voivodeship: { name: "Województwo" },
    county: { name: "Powiat" },
    municipality: { name: "Gmina" },
};

const LocationFilter = ({ groups, onFilterChange }) => {
    const [input, setInput] = useState("");
    const [suggestions, setSuggestions] = useState([]);

    const debouncedSearch = useMemo(
        () =>
            debounce((value) => {
                if (!value) {
                    setSuggestions([]);
                    onFilterChange({ voivodeship: "", county: "", municipality: "" });
                    return;
                }

                const lower = value.toLowerCase();
                const matches = groups
                    .flatMap((g) => [
                        { type: "voivodeship", value: g.voivodeship },
                        { type: "county", value: g.county },
                        { type: "municipality", value: g.municipality },
                    ])
                    .filter((item) => item.value?.toLowerCase().includes(lower));


                const uniqueSorted = Array.from(
                    new Map(matches.map((m) => [m.value, m])).values()
                ).sort((a, b) => a.value.localeCompare(b.value));

                setSuggestions(uniqueSorted);
            }, 300),
        [groups, onFilterChange]
    );

    const handleChange = (e) => {
        const value = e.target.value;
        setInput(value);
        debouncedSearch(value);

        onFilterChange({ voivodeship: value, county: value, municipality: value });
    };

    const handleSelect = (value) => {
        setInput(value);
        setSuggestions([]);
        onFilterChange({ voivodeship: value, county: value, municipality: value });
    };

    return (
        <Box sx={{ position: "relative", mb: 2 }}>
            <TextField
                fullWidth
                label="Lokalizacja (województwo, powiat, gmina)"
                value={input}
                onChange={handleChange}
                autoComplete="off"
            />
            {suggestions.length > 0 && (
                <List
                    sx={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        bgcolor: "background.paper",
                        border: "1px solid #ccc",
                        maxHeight: 200,
                        overflowY: "auto",
                        zIndex: 10,
                    }}
                >
                    {suggestions.map((s, idx) => (
                        <ListItem key={idx} disablePadding>
                            <ListItemButton onClick={() => handleSelect(s.value)}>
                                <Typography variant="body2">
                                    {s.value} <small>({LOCALIZATION_TYPES[s.type]?.name})</small>
                                </Typography>
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            )}
        </Box>
    );
};

const MyGroups = () => {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [error, setError] = useState(null);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);

    const [newGroupName, setNewGroupName] = useState("");
    const [newGroupDescription, setNewGroupDescription] = useState("");
    const [newGroupType, setNewGroupType] = useState("");

    const [addGroupDialogOpen, setAddGroupDialogOpen] = useState(false);
    const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
    const [importUsersDialogOpen, setImportUsersDialogOpen] = useState(false);
    const [generateDialogOpen, setGenerateDialogOpen] = useState(false);

    const [createLocation, setCreateLocation] = useState({
        voivodeship: "",
        county: "",
        municipality: "",
    });

    const [filterLocation, setFilterLocation] = useState({
        voivodeship: "",
        county: "",
        municipality: "",
    });


    // const storedUser = JSON.parse(localStorage.getItem("user"));
    // const token = storedUser?.jwt;

    const { user: storedUser, accessToken: token } = useSelector((state) => state.auth);

    const fetchGroups = async () => {
        try {
            setLoading(true);
            const res = await api.get(
                `${process.env.REACT_APP_API_URL}/users-permissions/auth/my-groups`,
                // { headers: { Authorization: `Bearer ${token}` } }
            );

            setGroups(res.data);
        } catch {
            setError("Nie udało się pobrać grup");
        } finally {
            setLoading(false);
        }
    };

    const generateUID = (name) => {
        if (!name) return "";
        const slug = name
            .toLowerCase()
            .replace(/\s+/g, "-")
            .replace(/[^a-z0-9\-]/g, "");
        const randomSuffix = Math.random().toString(36).substring(2, 6);
        return `${slug}-${randomSuffix}`;
    };

    const handleCreateGroup = async () => {
        console.log('handleCreateGroup')
        try {
            const groupData = {
                name: newGroupName,
                description: newGroupDescription,
                users: [storedUser.id],
                type: newGroupType,
                code: generateUID(newGroupName),
                voivodeship: createLocation.voivodeship,
                county: createLocation.county,
                municipality: createLocation.municipality,
            };
            console.log('isAnyFieldEmpty', Object.values(groupData));

            const isAnyFieldEmpty = Object.values(groupData).some(el => {
                if (Array.isArray(el)) return el.length === 0;
                return el === null || el === undefined || el === '';
            });

            console.log('isAnyFieldEmpty22', isAnyFieldEmpty);

            if (isAnyFieldEmpty) {
                setError("Wszystkie pola muszą być uzupełnione");
                return;
            }

            // const res = await axios.post(
            //     `${process.env.REACT_APP_API_URL}/groups`,
            //     { data: groupData },
            //     { headers: { Authorization: `Bearer ${token}` } }
            // );


            const res = await api.post(
                `${process.env.REACT_APP_API_URL}/groups`,
                { data: groupData });

            setGroups((prev) => [...prev, { id: res.data?.data?.id, ...res.data?.data?.attributes }]);
            setAddGroupDialogOpen(false);
            setNewGroupName("");
            setNewGroupDescription("");
            setCreateLocation({ voivodeship: "", county: "", municipality: "" });

        } catch {
            setError("Nie udało się utworzyć grupy");
        }
    };

    useEffect(() => {
        if (token) fetchGroups();
    }, [token]);

    // const filteredGroups = useMemo(() => {
    //     return groups.filter((group) => {
    //         const matchesSearch = group.name
    //             ?.toLowerCase()
    //             .includes(search.toLowerCase());

    //         console.log('filterLocation', filterLocation)

    //         const matchesVoivodeship = filterLocation.voivodeship
    //             ? group.voivodeship
    //                 ?.toLowerCase()
    //                 .includes(filterLocation.voivodeship.toLowerCase())
    //             : true;

    //         const matchesCounty = filterLocation.county
    //             ? group.county
    //                 ?.toLowerCase()
    //                 .includes(filterLocation.county.toLowerCase())
    //             : true;

    //         const matchesMunicipality = filterLocation.municipality
    //             ? group.municipality
    //                 ?.toLowerCase()
    //                 .includes(filterLocation.municipality.toLowerCase())
    //             : true;

    //         return (
    //             matchesSearch &&
    //             matchesVoivodeship &&
    //             matchesCounty &&
    //             matchesMunicipality
    //         );
    //     });
    // }, [groups, search, filterLocation]);

    const filteredGroups = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();

        return groups.filter((group) => {
            const matchesSearch =
                !normalizedSearch ||
                group.name?.toLowerCase().includes(normalizedSearch);

            const matchesLocation =
                !filterLocation.voivodeship &&
                    !filterLocation.county &&
                    !filterLocation.municipality
                    ? true
                    : (
                        group.voivodeship?.toLowerCase().includes(filterLocation.voivodeship.toLowerCase()) ||
                        group.county?.toLowerCase().includes(filterLocation.county.toLowerCase()) ||
                        group.municipality?.toLowerCase().includes(filterLocation.municipality.toLowerCase())
                    );

            return matchesSearch && matchesLocation;
        });
    }, [groups, search, filterLocation]);


    const handleDelete = async () => {
        try {
            // await axios.delete(
            //     `${process.env.REACT_APP_API_URL}/users-permissions/groups/${selectedGroup.id}/remove-user/${storedUser.id}`,
            //     { headers: { Authorization: `Bearer ${token}` } }
            // );

            await api.delete(`${process.env.REACT_APP_API_URL}/users-permissions/groups/${selectedGroup.id}/remove-user/${storedUser.id}`);

            setGroups((prev) => prev.filter((g) => g.id !== selectedGroup.id));
            setDeleteDialogOpen(false);
        } catch {
            setError("Nie udało się usunąć grupy");
        }
    };

    const clearFilters = () => {
        setFilterLocation({ voivodeship: "", county: "", municipality: "" });
        setSearch("");
    };

    return (
        <Box p={4}>
            <Box
                display="flex"
                justifyContent={storedUser.userRole === "admin" ? "space-between" : "center"}
                mb={3}
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: storedUser.userRole === "admin" ? "auto" : "100%" }}>
                    <Typography variant="h4" align={storedUser.userRole === "user" ? "center" : "left"}>
                        Moje grupy
                    </Typography>

                    {storedUser.userRole === "user" && (
                        <Alert
                            severity="info"
                            sx={{
                                color: "#555",
                                width: "1000px",
                                alignSelf: "center"
                            }}
                        >
                            Tutaj zobaczysz grupy, do których należysz. Tworzą je administratorzy i jednostki organizacyjne, które także dodają do nich uczestników.
                            <br />
                            Ty jako uczestnik widzisz tylko te grupy, w których jesteś członkiem.
                        </Alert>
                    )}
                </Box>

                {storedUser.userRole === "admin" && (
                    <Box>
                        <Button
                            sx={{ ml: 2 }}
                            variant="contained"
                            onClick={() => setAddGroupDialogOpen(true)}
                        >
                            Dodaj grupę
                        </Button>
                    </Box>
                )}
            </Box>


            <Dialog open={addUserDialogOpen} onClose={() => setAddUserDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Dodaj użytkownika</DialogTitle>
                <DialogContent sx={{ minHeight: 200 }}>
                    <UserSearchInput
                        token={token}
                        groupId={selectedGroup?.id}
                    // onSelect={(user) => {
                    //     console.log("Wybrany użytkownik:", user);
                    // }}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Generuj raport</DialogTitle>
                <DialogContent sx={{ minHeight: 200 }}>
                    <UsersReportList token={token} groupId={selectedGroup?.id} />
                </DialogContent>
            </Dialog>


            <Paper sx={{ p: 3 }}>
                <TextField
                    fullWidth
                    label="Szukaj grupy"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ mb: 2 }}
                />

                <LocationFilter groups={groups} onFilterChange={setFilterLocation} />

                {loading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : filteredGroups.length === 0 ? (
                    <Typography align="center" py={4}>
                        Brak grup
                    </Typography>
                ) : (
                    <TableContainer sx={{ mt: 3 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Nazwa</TableCell>
                                    <TableCell>Typ</TableCell>
                                    <TableCell>Województwo</TableCell>
                                    <TableCell>Powiat</TableCell>
                                    <TableCell>Gmina</TableCell>
                                    {storedUser.userRole === "admin" && (
                                        <TableCell align="right">Akcje</TableCell>
                                    )}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {filteredGroups.map((group) => (
                                    <TableRow key={group.id}>
                                        <TableCell>{group.name}</TableCell>
                                        <TableCell>{GROUP_TYPES[group.type]?.name || group.type}</TableCell>
                                        <TableCell>{group.voivodeship || "-"}</TableCell>
                                        <TableCell>{group.county || "-"}</TableCell>
                                        <TableCell>{group.municipality || "-"}</TableCell>
                                        {storedUser.userRole === "admin" && (
                                            <TableCell align="right">
                                                <Tooltip describeChild title="Wygeneruj raport" placement="top">
                                                    <IconButton
                                                        color="info"
                                                        onClick={() => {
                                                            setSelectedGroup(group);
                                                            setGenerateDialogOpen(true);
                                                        }}
                                                    >
                                                        <DescriptionIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip describeChild title="Dodaj użytkownika" placement="top">
                                                    <IconButton
                                                        color="info"
                                                        onClick={() => {
                                                            setSelectedGroup(group);
                                                            setAddUserDialogOpen(true);
                                                        }}
                                                    >
                                                        <PersonAddAltIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip describeChild title="Importuj użytkowników z pliku" placement="top">
                                                    <IconButton
                                                        color="info"
                                                        onClick={() => {
                                                            setSelectedGroup(group);
                                                            setImportUsersDialogOpen(true);
                                                        }}
                                                    >
                                                        <GroupAddIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip describeChild title="Usuń grupę" placement="top">
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => {
                                                            setSelectedGroup(group);
                                                            setDeleteDialogOpen(true);
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            <Dialog open={addGroupDialogOpen} onClose={() => setAddGroupDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Dodaj grupę</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Nazwa"
                        sx={{ mb: 2 }}
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                    />
                    <TextField
                        fullWidth
                        label="Opis"
                        sx={{ mb: 2 }}
                        value={newGroupDescription}
                        onChange={(e) => setNewGroupDescription(e.target.value)}
                    />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Typ grupy</InputLabel>
                        <Select value={newGroupType} label="Typ grupy" onChange={(e) => setNewGroupType(e.target.value)}>
                            <MenuItem value="trainingGroup">Grupa szkoleniowa</MenuItem>
                            <MenuItem value="event">Grupa na wydarzenie</MenuItem>
                        </Select>
                    </FormControl>
                    <PostalCodeForm filterLocation={setCreateLocation} addError={error} />
                    <SelectionMap location={createLocation} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddGroupDialogOpen(false)}>Anuluj</Button>
                    <Button variant="contained" onClick={handleCreateGroup}>
                        Dodaj
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Usuń grupę</DialogTitle>
                <DialogContent>
                    Czy na pewno chcesz usunąć grupę:{" "}
                    <strong>{selectedGroup?.name}</strong>?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Anuluj</Button>
                    <Button
                        onClick={handleDelete}
                        color="error"
                        variant="contained"
                    >
                        Usuń
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={importUsersDialogOpen} onClose={() => setImportUsersDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Importuj użytkowników</DialogTitle>
                <DialogContent>
                    <ImportUsersDialog
                        token={token}
                        open={importUsersDialogOpen}
                        onClose={() => setImportUsersDialogOpen(false)}
                        selectedGroupCode={selectedGroup?.code}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddGroupDialogOpen(false)}>Anuluj</Button>
                    <Button variant="contained" onClick={handleCreateGroup}>
                        Dodaj
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MyGroups;

// import React, { useEffect, useState, useMemo } from "react";
// import axios from "axios";
// import {
//     Box,
//     Typography,
//     Paper,
//     CircularProgress,
//     TextField,
//     IconButton,
//     Tooltip,
//     Table,
//     TableHead,
//     TableRow,
//     TableCell,
//     TableBody,
//     TableContainer,
//     Button,
//     Dialog,
//     DialogTitle,
//     DialogContent,
//     DialogActions,
//     Alert,
//     FormControl,
//     InputLabel,
//     Select,
//     MenuItem,
// } from "@mui/material";
// import RefreshIcon from "@mui/icons-material/Refresh";
// import DeleteIcon from "@mui/icons-material/Delete";
// import PostalCodeForm from "../../Components/groups/PostalCodeForm";
// import { code } from "three/src/nodes/TSL.js";

// const MyGroups = () => {
//     const [groups, setGroups] = useState([]);
//     const [loading, setLoading] = useState(true);
//     const [search, setSearch] = useState("");
//     const [error, setError] = useState(null);

//     const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
//     const [selectedGroup, setSelectedGroup] = useState(null);

//     const [addGroupDialogOpen, setAddGroupDialogOpen] = useState(false);
//     const [newGroupName, setNewGroupName] = useState("");
//     const [newGroupDescription, setNewGroupDescription] = useState("");
//     const [newGroupType, setNewGroupType] = useState("");

//     const [postalCodeData, setPostalCodeData] = useState({
//         province: "",
//         county: "",
//         commune: "",
//     });

//     const storedUser = JSON.parse(localStorage.getItem("user"));
//     const token = storedUser?.jwt;

//     const fetchGroups = async () => {
//         try {
//             setLoading(true);
//             const res = await axios.get(
//                 `${process.env.REACT_APP_API_URL}/users-permissions/auth/my-groups`,
//                 {
//                     headers: { Authorization: `Bearer ${token}` },
//                 }
//             );
//             setGroups(res.data);
//         } catch (err) {
//             setError("Nie udało się pobrać grup");
//         } finally {
//             setLoading(false);
//         }
//     };

//     const generateUID = (name) => {
//         if (!name) return "";
//         const slug = name
//             .toLowerCase()
//             .replace(/\s+/g, "-")
//             .replace(/[^a-z0-9\-]/g, "");
//         const randomSuffix = Math.random().toString(36).substring(2, 6);
//         return `${slug}-${randomSuffix}`;
//     };

//     console.log('postalCodeData', postalCodeData)

//     const handleCreateGroup = async () => {
//         try {
//             const groupData = {
//                 name: newGroupName,
//                 description: newGroupDescription,
//                 users: [storedUser.id],
//                 type: newGroupType,
//                 code: generateUID(newGroupName),
//                 voivodeship: postalCodeData.province,
//                 county: postalCodeData.county,
//                 municipality: postalCodeData.commune,
//             };
//             const res = await axios.post(
//                 `${process.env.REACT_APP_API_URL}/groups`,
//                 { data: groupData },
//                 {
//                     headers: { Authorization: `Bearer ${token}` },
//                 }
//             );
//             setGroups((prev) => [...prev, res.data]);
//             setAddGroupDialogOpen(false);
//             setNewGroupName("");
//             setNewGroupDescription("");
//         } catch (err) {
//             setError("Nie udało się utworzyć grupy");
//         }
//     };

//     useEffect(() => {
//         if (token) fetchGroups();
//     }, [token]);

//     const filteredGroups = useMemo(() => {
//         return groups.filter((group) =>
//             group.name?.toLowerCase().includes(search.toLowerCase())
//         );
//     }, [groups, search]);

//     const handleAddGroup = () => {
//         setAddGroupDialogOpen(true);
//     };

//     const handleDelete = async () => {
//         try {
//             await axios.delete(
//                 `${process.env.REACT_APP_API_URL}/groups/${selectedGroup.id}`,
//                 {
//                     headers: { Authorization: `Bearer ${token}` },
//                 }
//             );
//             setGroups((prev) =>
//                 prev.filter((g) => g.id !== selectedGroup.id)
//             );
//             setDeleteDialogOpen(false);
//             setSelectedGroup(null);
//         } catch (err) {
//             setError("Nie udało się usunąć grupy");
//         }
//     };

//     return (
//         <Box p={4}>
//             <Box
//                 display="flex"
//                 justifyContent="space-between"
//                 alignItems="center"
//                 mb={3}
//             >
//                 <Typography variant="h4">Moje grupy</Typography>

//                 <Tooltip title="Odśwież">
//                     <IconButton onClick={fetchGroups}>
//                         <RefreshIcon />
//                     </IconButton>
//                 </Tooltip>
//             </Box>

//             <Button variant="contained" color="primary" onClick={handleAddGroup}>
//                 Dodaj grupę
//             </Button>

//             <Paper sx={{ p: 3, mt: 2 }}>
//                 <Box mb={2}>
//                     <TextField
//                         fullWidth
//                         label="Szukaj grupy"
//                         value={search}
//                         onChange={(e) => setSearch(e.target.value)}
//                     />
//                 </Box>

//                 {loading ? (
//                     <Box display="flex" justifyContent="center" py={4}>
//                         <CircularProgress />
//                     </Box>
//                 ) : filteredGroups.length === 0 ? (
//                     <Typography align="center" py={4}>
//                         Brak grup
//                     </Typography>
//                 ) : (
//                     <TableContainer>
//                         <Table>
//                             <TableHead>
//                                 <TableRow>
//                                     <TableCell>Nazwa</TableCell>
//                                     <TableCell>Opis</TableCell>
//                                     <TableCell align="right">Akcje</TableCell>
//                                 </TableRow>
//                             </TableHead>

//                             <TableBody>
//                                 {filteredGroups.map((group) => (
//                                     <TableRow key={group.id}>
//                                         <TableCell>{group.name}</TableCell>
//                                         <TableCell>
//                                             {group.description || "-"}
//                                         </TableCell>
//                                         <TableCell align="right">
//                                             <Tooltip title="Usuń grupę">
//                                                 <IconButton
//                                                     color="error"
//                                                     onClick={() => {
//                                                         setSelectedGroup(group);
//                                                         setDeleteDialogOpen(true);
//                                                     }}
//                                                 >
//                                                     <DeleteIcon />
//                                                 </IconButton>
//                                             </Tooltip>
//                                         </TableCell>
//                                     </TableRow>
//                                 ))}
//                             </TableBody>
//                         </Table>
//                     </TableContainer>
//                 )}
//             </Paper>

//             {error && (
//                 <Box mt={2}>
//                     <Alert severity="error">{error}</Alert>
//                 </Box>
//             )}

//             <Dialog
//                 open={addGroupDialogOpen}
//                 onClose={() => setAddGroupDialogOpen(false)}
//                 maxWidth="sm"
//                 fullWidth
//             >
//                 <DialogTitle>Dodaj grupę</DialogTitle>
//                 <DialogContent>
//                     <Box mb={2}>
//                         <TextField
//                             fullWidth
//                             label="Nazwa"
//                             variant="outlined"
//                             value={newGroupName}
//                             onChange={(e) => setNewGroupName(e.target.value)}
//                         />
//                     </Box>
//                     <Box mb={2}>
//                         <TextField
//                             fullWidth
//                             label="Opis"
//                             variant="outlined"
//                             value={newGroupDescription}
//                             onChange={(e) => setNewGroupDescription(e.target.value)}
//                         />
//                     </Box>
//                     <Box mb={2}>
//                         <FormControl fullWidth>
//                             <InputLabel id="select-group-type">Typ grupy</InputLabel>
//                             <Select
//                                 labelId="select-group-type"
//                                 id="select-group"
//                                 value={newGroupType}
//                                 label="Typ grupy"
//                                 onChange={(e) => setNewGroupType(e.target.value)}
//                             >
//                                 <MenuItem value={"trainingGroup"}>Grupa szkoleniowa</MenuItem>
//                                 <MenuItem value={"event"}>Grupa na wydarzenie</MenuItem>
//                             </Select>
//                         </FormControl>
//                     </Box>
//                     <PostalCodeForm setPostalCodeData={setPostalCodeData} />
//                 </DialogContent>
//                 <DialogActions>
//                     <Button onClick={() => setAddGroupDialogOpen(false)}>Anuluj</Button>
//                     <Button
//                         variant="contained"
//                         color="primary"
//                         onClick={handleCreateGroup}
//                     >
//                         Dodaj
//                     </Button>
//                 </DialogActions>
//             </Dialog>

//             {/* Dialog usuwania grupy */}
//             <Dialog
//                 open={deleteDialogOpen}
//                 onClose={() => setDeleteDialogOpen(false)}
//             >
//                 <DialogTitle>Usuń grupę</DialogTitle>
//                 <DialogContent>
//                     Czy na pewno chcesz usunąć grupę:{" "}
//                     <strong>{selectedGroup?.name}</strong>?
//                 </DialogContent>
//                 <DialogActions>
//                     <Button onClick={() => setDeleteDialogOpen(false)}>Anuluj</Button>
//                     <Button
//                         onClick={handleDelete}
//                         color="error"
//                         variant="contained"
//                     >
//                         Usuń
//                     </Button>
//                 </DialogActions>
//             </Dialog>
//         </Box>
//     );
// };

// export default MyGroups;


// import React, { useEffect, useState, useMemo } from "react";
// import axios from "axios";
// import {
//     Box,
//     Typography,
//     Paper,
//     CircularProgress,
//     TextField,
//     IconButton,
//     Tooltip,
//     Table,
//     TableHead,
//     TableRow,
//     TableCell,
//     TableBody,
//     TableContainer,
//     Button,
//     Dialog,
//     DialogTitle,
//     DialogContent,
//     DialogActions,
//     Alert,
// } from "@mui/material";
// import RefreshIcon from "@mui/icons-material/Refresh";
// import DeleteIcon from "@mui/icons-material/Delete";
// import SelectionMap from "../../Components/groups/SelectionMap";
// import PostalCodeForm from "../../Components/groups/PostalCodeForm";

// const MyGroups = () => {
//     const [groups, setGroups] = useState([]);
//     const [loading, setLoading] = useState(true);
//     const [search, setSearch] = useState("");
//     const [error, setError] = useState(null);

//     const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
//     const [selectedGroup, setSelectedGroup] = useState(null);

//     const [addGroupDialogOpen, setAddGroupDialogOpen] = useState(false);

//     const storedUser = JSON.parse(localStorage.getItem("user"));
//     const token = storedUser?.jwt;

//     const fetchGroups = async () => {
//         try {
//             setLoading(true);
//             const res = await axios.get(
//                 `${process.env.REACT_APP_API_URL}/users-permissions/auth/my-groups`,
//                 {
//                     headers: { Authorization: `Bearer ${token}` },
//                 }
//             );
//             setGroups(res.data);
//         } catch (err) {
//             setError("Nie udało się pobrać grup");
//         } finally {
//             setLoading(false);
//         }
//     };

//     const handleCreateGroup = async (groupData) => {
//         try {
//             const res = await axios.post(
//                 `${process.env.REACT_APP_API_URL}/groups`,
//                 groupData,
//                 {
//                     headers: { Authorization: `Bearer ${token}` },
//                 }
//             );
//             setGroups((prev) => [...prev, res.data]);
//             setAddGroupDialogOpen(false);
//         } catch (err) {
//             setError("Nie udało się utworzyć grupy");
//         }
//     };

//     useEffect(() => {
//         if (token) fetchGroups();
//     }, [token]);

//     const filteredGroups = useMemo(() => {
//         return groups.filter((group) =>
//             group.name?.toLowerCase().includes(search.toLowerCase())
//         );
//     }, [groups, search]);

//     const handleAddGroup = () => {
//         setAddGroupDialogOpen(true);
//     };

//     const handleDelete = async () => {
//         try {
//             await axios.delete(
//                 `${process.env.REACT_APP_API_URL}/groups/${selectedGroup.id}`,
//                 {
//                     headers: { Authorization: `Bearer ${token}` },
//                 }
//             );
//             setGroups((prev) =>
//                 prev.filter((g) => g.id !== selectedGroup.id)
//             );
//             setDeleteDialogOpen(false);
//             setSelectedGroup(null);
//         } catch (err) {
//             setError("Nie udało się usunąć grupy");
//         }
//     };

//     return (
//         <Box p={4}>
//             <Box
//                 display="flex"
//                 justifyContent="space-between"
//                 alignItems="center"
//                 mb={3}
//             >
//                 <Typography variant="h4">Moje grupy</Typography>

//                 <Tooltip title="Odśwież">
//                     <IconButton onClick={fetchGroups}>
//                         <RefreshIcon />
//                     </IconButton>
//                 </Tooltip>
//             </Box>

//             <Button variant="contained" color="primary" onClick={handleAddGroup}>
//                 Dodaj grupę
//             </Button>

//             <Paper sx={{ p: 3, mt: 2 }}>
//                 <Box mb={2}>
//                     <TextField
//                         fullWidth
//                         label="Szukaj grupy"
//                         value={search}
//                         onChange={(e) => setSearch(e.target.value)}
//                     />
//                 </Box>

//                 {loading ? (
//                     <Box display="flex" justifyContent="center" py={4}>
//                         <CircularProgress />
//                     </Box>
//                 ) : filteredGroups.length === 0 ? (
//                     <Typography align="center" py={4}>
//                         Brak grup
//                     </Typography>
//                 ) : (
//                     <TableContainer>
//                         <Table>
//                             <TableHead>
//                                 <TableRow>
//                                     <TableCell>Nazwa</TableCell>
//                                     <TableCell>Opis</TableCell>
//                                     <TableCell align="right">Akcje</TableCell>
//                                 </TableRow>
//                             </TableHead>

//                             <TableBody>
//                                 {filteredGroups.map((group) => (
//                                     <TableRow key={group.id}>
//                                         <TableCell>{group.name}</TableCell>
//                                         <TableCell>
//                                             {group.description || "-"}
//                                         </TableCell>
//                                         <TableCell align="right">
//                                             <Tooltip title="Usuń grupę">
//                                                 <IconButton
//                                                     color="error"
//                                                     onClick={() => {
//                                                         setSelectedGroup(group);
//                                                         setDeleteDialogOpen(true);
//                                                     }}
//                                                 >
//                                                     <DeleteIcon />
//                                                 </IconButton>
//                                             </Tooltip>
//                                         </TableCell>
//                                     </TableRow>
//                                 ))}
//                             </TableBody>
//                         </Table>
//                     </TableContainer>
//                 )}
//             </Paper>

//             {error && (
//                 <Box mt={2}>
//                     <Alert severity="error">{error}</Alert>
//                 </Box>
//             )}

//             <Dialog
//                 open={addGroupDialogOpen}
//                 onClose={() => setAddGroupDialogOpen(false)}
//                 maxWidth="sm"
//                 fullWidth
//             >
//                 <DialogTitle>Dodaj grupę</DialogTitle>
//                 <DialogContent>
//                     <TextField
//                                         label="Nazwa"
//                                         variant="outlined"
//                                         value={name}
//                                         InputProps={{ readOnly: true }}
//                                     />
//                     <PostalCodeForm />
//                     {/* <SelectionMap /> */}
//                 </DialogContent>
//                 <DialogActions>
//                     <Button onClick={() => setAddGroupDialogOpen(false)}>
//                         Anuluj
//                     </Button>
//                     <Button
//                         variant="contained"
//                         color="primary"
//                         onClick={() => handleCreateGroup}
//                     >
//                         Dodaj
//                     </Button>
//                 </DialogActions>
//             </Dialog>



//             <Dialog
//                 open={deleteDialogOpen}
//                 onClose={() => setDeleteDialogOpen(false)}
//             >
//                 <DialogTitle>Usuń grupę</DialogTitle>
//                 <DialogContent>
//                     Czy na pewno chcesz usunąć grupę:{" "}
//                     <strong>{selectedGroup?.name}</strong>?
//                 </DialogContent>
//                 <DialogActions>
//                     <Button onClick={() => setDeleteDialogOpen(false)}>
//                         Anuluj
//                     </Button>
//                     <Button
//                         onClick={handleDelete}
//                         color="error"
//                         variant="contained"
//                     >
//                         Usuń
//                     </Button>
//                 </DialogActions>
//             </Dialog>
//         </Box>
//     );
// };

// export default MyGroups;